import 'core-js/stable'
import 'rc-progress/assets/index.css'

import { ErrorBoundary as ErrorTrackingBoundary } from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import axios from 'axios'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { Store } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { Userpilot } from 'userpilot'

import { getGGClient } from '@/clients/gg/GetGoingClient'
import { Routes } from '@/lib/routes'
import { IAction } from '@/redux/actions'
import { Actions as RootActions } from '@/redux/actions/root.actions'
import { createApplicationStore } from '@/redux/stores'
import { ApplicationState } from '@/redux/stores/types'

import { appConfig } from './app-config/appConfig'
import { ErrorBoundary } from './error-tracking/ErrorBoundary'
import { initializeErrorTracking } from './error-tracking/initializeErrorTracking'
import { initializeI18n } from './i18nConfig'
import {
	createGetGoingAuthFailedResponseInterceptor,
	createGetGoingAuthRequestInterceptor,
	getAuthPreloadedState,
	handleIE,
	handleUserReloadRequest,
	initializeAuthProvider,
	tryHashQueryLogin,
} from './lib/auth'
import { isMobile } from './lib/browser'
import { IOSEventType, isLaunchedAsIOSApp, subscribeForIOSEvent } from './lib/ios'
import { redirectTo } from './lib/route-utils'
import App from './pages/App/App'
import { GeneralErrorPage } from './pages/GeneralErrorPage'
import { registerServiceWorker } from './pwa/registerServiceWorker'
import { StylesProvider } from './StylesProvider'
import { UserTrackingClient } from './tracking/types'
import { UserTrackingContext } from './tracking/UserTrackingContext'

function wrapWithErrorBoundary(node: React.ReactNode): React.ReactNode {
	if (!appConfig.SENTRY_ENABLED) {
		return <ErrorBoundary fallback={<GeneralErrorPage errorId="" />}>{node}</ErrorBoundary>
	}

	return (
		<ErrorTrackingBoundary fallback={({ eventId }) => <GeneralErrorPage errorId={eventId} />}>
			{node}
		</ErrorTrackingBoundary>
	)
}

document.addEventListener('DOMContentLoaded', async () => {
	if (appConfig.SENTRY_ENABLED) {
		initializeErrorTracking()
	}

	initializeI18n()
	initializeAuthProvider()

	if ('serviceWorker' in navigator) {
		registerServiceWorker()
	}

	if (isLaunchedAsIOSApp()) {
		subscribeForIOSEvent(IOSEventType.RedirectToTrip, ({ detail }) => {
			redirectTo(Routes.Itinerary, { id: Number(detail) })
		})
	}

	const trackingClient: UserTrackingClient | null = appConfig.USERPILOT_KEY
		? { reload: Userpilot.reload, identify: (userId, meta) => Userpilot.identify(String(userId), meta) }
		: null

	if (appConfig.USERPILOT_KEY) {
		Userpilot.initialize(appConfig.USERPILOT_KEY)
	}

	const store = createApplicationStore({} as ApplicationState, import.meta.env.PROD ? undefined : composeWithDevTools)
	store.dispatch(RootActions.clearStore({ auth: getAuthPreloadedState(store) }))

	getGGClient().setStore(store)

	initAxios(store)

	handleIE(store)

	await tryHashQueryLogin(store)

	await handleUserReloadRequest(store)

	const queryClient = new QueryClient()

	const root = createRoot(document.getElementById('root')!)

	root.render(
		<StylesProvider>
			{wrapWithErrorBoundary(
				<UserTrackingContext.Provider value={trackingClient}>
					<ReduxProvider store={store}>
						<QueryClientProvider client={queryClient}>
							<App />
							<ReactQueryDevtools initialIsOpen={false} />
						</QueryClientProvider>
					</ReduxProvider>
				</UserTrackingContext.Provider>,
			)}
		</StylesProvider>,
	)

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const _w: any = window
	if (isMobile() && _w.Intercom) {
		_w.Intercom('update', {
			hide_default_launcher: true,
		})
	}
})

function initAxios(store: Store<ApplicationState, IAction>) {
	axios.interceptors.request.use((config) => {
		return { ...config, validateStatus: (status: number) => status >= 200 && status < 400 }
	})
	axios.interceptors.request.use(createGetGoingAuthRequestInterceptor())

	axios.interceptors.response.use(
		undefined,
		createGetGoingAuthFailedResponseInterceptor(() => store),
	)
}
